/**
 * Campaign switcher dropdown.
 *
 * LLui's standard menu owns trigger/open state, keyboard/typeahead navigation,
 * positioning, dismissal, and focus return. DungeonLogs supplies the campaign
 * rows and the route/reload actions selected from them.
 */
import { button as uiButton, dropdownMenu, type DropdownMenuOptions } from '@dl/ui';
import {
  derived,
  div,
  each,
  li,
  span,
  text,
  ul,
  type Mountable,
  type Send,
  type Signal,
} from '@llui/dom';

import { t } from '../i18n.js';

import { monogram } from './accent.js';
import { enterCampaign } from './picker-view.js';
import { pickSwitcherCampaigns, type SwitcherStateShape } from './switcher-state.js';

interface SwitcherMsgFns<Msg> {
  navigateToPicker: () => Msg;
}

interface SwitcherRow {
  id: string;
  name: string;
  isCurrent: boolean;
  mono: string;
}

interface SwitcherSnapshot {
  key: string;
  rows: SwitcherRow[];
}

function switcherSnapshot(s: SwitcherStateShape, currentId: string | null): SwitcherSnapshot {
  const rows = pickSwitcherCampaigns(s).map((campaign) => ({
    id: campaign.id,
    name: campaign.name,
    isCurrent: campaign.id === currentId,
    mono: monogram(campaign.name),
  }));
  return {
    key: rows.map((row) => `${row.id}:${row.name}:${row.isCurrent ? '1' : '0'}`).join('|'),
    rows,
  };
}

function viewSwitcherMenu<Msg>(
  send: Send<Msg>,
  snapshot: SwitcherSnapshot,
  currentId: string | null,
  msgFns: SwitcherMsgFns<Msg>,
): Mountable {
  const current = snapshot.rows.find((row) => row.isCurrent);
  const items = [
    ...snapshot.rows.map((row) => ({ value: `campaign:${row.id}`, kind: 'action' as const })),
    { value: 'create', kind: 'action' as const },
    { value: 'manage', kind: 'action' as const },
  ] satisfies DropdownMenuOptions['items'];

  return dropdownMenu({
    id: 'campaign-switcher-menu',
    items,
    placement: 'bottom-end',
    panelClass: 'campaign-switcher-menu',
    testid: 'campaign-switcher-menu',
    trigger: (trigger) =>
      uiButton({
        variant: 'ghost',
        className: 'campaign-switcher-button',
        testid: 'campaign-switcher-button',
        ariaLabel: t('campaigns.switcher.label'),
        attrs: { ...trigger },
        children: [
          span({ class: 'campaign-switcher-mark', 'aria-hidden': 'true' }, [
            text(current?.mono ?? monogram(currentId ?? '')),
          ]),
          span({ class: 'campaign-switcher-body' }, [
            span({ class: 'campaign-switcher-name' }, [
              text(current?.name ?? currentId ?? ''),
            ]),
            span({ class: 'campaign-switcher-sub' }, [
              text(t('campaigns.switcher.count', { count: snapshot.rows.length })),
            ]),
          ]),
          span({ class: 'campaign-switcher-chev', 'aria-hidden': 'true' }, ['▾']),
        ],
      }),
    children: (parts) => [
      span(
        {
          class: 'campaign-switcher-heading',
          role: 'presentation',
          'aria-hidden': 'true',
        },
        [text(t('campaigns.switcher.heading'))],
      ),
      ul(
        { class: 'campaign-switcher-list' },
        snapshot.rows.map((row) =>
          li({ role: 'none', class: 'campaign-switcher-item' }, [
            uiButton({
              variant: 'ghost',
              className: 'campaign-switcher-row',
              testid: 'campaign-switcher-row',
              attrs: {
                ...parts.item(`campaign:${row.id}`).item,
                'data-current': row.isCurrent ? 'true' : 'false',
                'aria-current': row.isCurrent ? 'page' : undefined,
              },
              children: [
                span({ class: 'campaign-switcher-row-mark', 'aria-hidden': 'true' }, [
                  text(row.mono),
                ]),
                span({ class: 'campaign-switcher-row-name' }, [text(row.name)]),
              ],
            }),
          ]),
        ),
      ),
      ...(snapshot.rows.length <= 1
        ? [
            span(
              {
                class: 'campaign-switcher-hint',
                role: 'presentation',
                'data-testid': 'campaign-switcher-only-one-hint',
              },
              [text(t('campaigns.switcher.only_one_hint'))],
            ),
          ]
        : []),
      uiButton({
        variant: 'ghost',
        className: 'campaign-switcher-create',
        testid: 'campaign-switcher-create',
        attrs: { ...parts.item('create').item },
        children: [text(t('campaigns.switcher.create'))],
      }),
      uiButton({
        variant: 'ghost',
        className: 'campaign-switcher-manage',
        testid: 'campaign-switcher-manage',
        attrs: { ...parts.item('manage').item },
        children: [text(t('campaigns.switcher.manage'))],
      }),
    ],
    onSelect: (value) => {
      if (value === 'create' || value === 'manage') {
        send(msgFns.navigateToPicker());
        return;
      }
      if (!value.startsWith('campaign:')) return;
      const id = value.slice('campaign:'.length);
      if (id !== currentId) enterCampaign(id);
    },
  });
}

export function viewCampaignSwitcher<Model extends SwitcherStateShape, Msg>(
  state: Signal<Model>,
  send: Send<Msg>,
  currentId: string | null,
  msgFns: SwitcherMsgFns<Msg>,
): Mountable {
  return div({ class: 'campaign-switcher', 'data-testid': 'campaign-switcher' }, [
    each(state.map((s) => [switcherSnapshot(s, currentId)]), {
      key: (snapshot) => snapshot.key,
      render: (item) => {
        const snapshot = derived([item], (value) => value).peek();
        return [
          div({ class: 'campaign-switcher-boundary' }, [
            viewSwitcherMenu(send, snapshot, currentId, msgFns),
          ]),
        ];
      },
    }),
  ]);
}
